import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/movies";
import type { HeroSlide } from "@/types/movie";

const slides = heroSlides as unknown as HeroSlide[];

export default function HeroCarousel() {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const go = (dir: number) => {
    setCurrent((prev) => (prev + dir + slides.length) % slides.length);
  };

  return (
    <section className="relative h-[440px] w-full overflow-hidden rounded-xl bg-primary-950 md:h-[560px]">
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === current ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={slide.image}
            alt={`${slide.title} 焦点海报`}
            title={`${slide.title} 91免费看片`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/85 via-primary-950/45 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/70 via-transparent to-transparent" />
        </div>
      ))}

      {/* Content */}
      <div className="relative z-10 flex h-full w-full flex-col justify-end px-6 pb-12 md:px-12 md:pb-16">
        <div key={slides[current].id} className="max-w-2xl animate-fade-up">
          <span className="inline-flex items-center gap-1 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
            <i className="ri-fire-fill" />
            {slides[current].tag}
          </span>
          <h2 className="mt-4 font-heading text-3xl font-extrabold text-white md:text-5xl">
            {slides[current].title}
          </h2>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/80 md:text-base">
            {slides[current].subtitle}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${slides[current].movieId}`}
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
            >
              <i className="ri-play-fill text-lg" />
              立即观看
            </Link>
            <Link
              to="/special"
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-white/40 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-white/10"
            >
              <i className="ri-bookmark-3-line" />
              影视专题
            </Link>
          </div>
        </div>
      </div>

      {/* Arrows */}
      <button
        type="button"
        aria-label="上一张"
        onClick={() => go(-1)}
        className="absolute left-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/35 text-white transition-colors hover:bg-black/55 md:left-6 md:h-11 md:w-11"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => go(1)}
        className="absolute right-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/35 text-white transition-colors hover:bg-black/55 md:right-6 md:h-11 md:w-11"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      {/* Dots */}
      <div className="absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
        {slides.map((slide, index) => (
          <button
            key={slide.id}
            type="button"
            aria-label={`切换到第${index + 1}张`}
            onClick={() => setCurrent(index)}
            className={`h-1.5 cursor-pointer rounded-full transition-all ${
              index === current ? "w-7 bg-accent-500" : "w-3 bg-white/50"
            }`}
          />
        ))}
      </div>
    </section>
  );
}